import { computed, unref, ref, watch } from 'vue';
import { ListSortOptions, ListSortOrders, LayerGroup, PurchaseStatus } from './useConstants';
import { useAnalytics } from './useAnalytics';
import { useI18n } from 'vue-i18n';
import debounce from 'debounce';

const REMOTE_SEARCH_DEBOUNCE_MS = 500;
const LOG_DEBOUNCE_MS = 1000;

const TYPE_SORT_ORDER = [
    'Waypoint',
    'Track',
    'Drawing',
    LayerGroup.Subscription,
    LayerGroup.BuiltInLayer,
    LayerGroup.WMU,
    LayerGroup.LEH,
    LayerGroup.County,

    PurchaseStatus.PurchasedUpradable,
    PurchaseStatus.PurchasedLatest,
    PurchaseStatus.NotPurchased,
    PurchaseStatus.Unavailable,
];

export function useList(items, searchTerm, activeSort, options = {}) {
    const { t, n } = useI18n();
    const { logSearch } = useAnalytics();

    const { alphaKey = 'name', dateKey = 'createdAt', activeKey = 'visible', typeKey = 'type' } = options;

    const { remoteSearchCallback } = options; // Optionally provide a callback function for performing remote searches instead of locally filtering the list
    const remoteResults = ref([]);
    const useRemoteSearch = computed(() => !!unref(remoteSearchCallback));

    const { eventParams = {} } = options; // Search analytics

    const getNestedValue = (obj, key) => {
        // If the key is not a string, stop here and return undefined.
        if (typeof key !== 'string' || !key) {
            return undefined;
        }
        return key.split('.').reduce((o, i) => (o ? o[i] : undefined), obj);
    };

    const compareStringValues = (a, b, key) => {
        return String(getNestedValue(a, key)).localeCompare(String(getNestedValue(b, key)));
    };

    const toTimestamp = (val) => {
        if (val == null || val === '') return NaN;
        if (typeof val === 'number') return val;
        if (val instanceof Date) return val.getTime();

        const parsed = Date.parse(val);
        if (!Number.isNaN(parsed)) return parsed;

        const num = Number(val);
        return Number.isNaN(num) ? NaN : num;
    };

    const compareDateValues = (a, b, key) => {
        const timeA = toTimestamp(getNestedValue(a, key));
        const timeB = toTimestamp(getNestedValue(b, key));

        if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
        if (Number.isNaN(timeA)) return 1;
        if (Number.isNaN(timeB)) return -1;

        return timeA - timeB;
    };

    const compareTypeValues = (a, b, key) => {
        const indexA = getTypeSortIndex(a, key);
        const indexB = getTypeSortIndex(b, key);
        // fallback to alpha sort if no TYPE_SORT_ORDER is defined
        return indexA === -1 || indexB === -1 ? compareStringValues(a, b, key) : indexA - indexB;
    };

    const getTypeSortIndex = (item, typeKey) => {
        const typeValue = getNestedValue(item, typeKey);
        return TYPE_SORT_ORDER.indexOf(typeValue);
    };

    const getFilteredList = () => {
        if (!items.value) return [];

        const searchLower = searchTerm?.value?.toLowerCase()?.trim();
        if (searchLower) {
            const list = items.value.filter((x) => {
                const keys = Array.isArray(alphaKey) ? alphaKey : [alphaKey];
                return keys.some((key) => getNestedValue(x, key)?.toLowerCase()?.includes(searchLower));
            });
            debounceLogSearch();
            return list;
        }
        return [...items.value];
    };

    const handleSortToggle = (newSortKey) => {
        if (!activeSort?.value) {
            return;
        }

        const currentSort = activeSort.value || ListSortOptions.All;

        if (newSortKey === ListSortOptions.Name) {
            if (currentSort === ListSortOrders.AscName || currentSort === ListSortOptions.Name) {
                activeSort.value = ListSortOrders.DescName;
            } else {
                activeSort.value = ListSortOrders.AscName;
            }
        } else if (newSortKey === ListSortOptions.Active) {
            if (currentSort === ListSortOrders.Active || currentSort === ListSortOptions.Active) {
                activeSort.value = ListSortOrders.NotActive;
            } else {
                activeSort.value = ListSortOrders.Active;
            }
        } else if (newSortKey === ListSortOptions.Date) {
            if (currentSort === ListSortOrders.NewestDate || currentSort === ListSortOptions.Date) {
                activeSort.value = ListSortOrders.OldestDate;
            } else {
                activeSort.value = ListSortOrders.NewestDate;
            }
        } else if (newSortKey === ListSortOptions.Type) {
            if (currentSort === ListSortOrders.AscType || currentSort === ListSortOptions.Type) {
                activeSort.value = ListSortOrders.DescType;
            } else {
                activeSort.value = ListSortOrders.AscType;
            }
        } else {
            activeSort.value = newSortKey;
        }
    };

    const processedList = computed(() => {
        if (useRemoteSearch.value) {
            // The remote query must be done asynchronously, so we can't just call the remote search callback directly here.
            // The watcher will update remoteResults when the search term changes.
            return remoteResults.value;
        }

        const filteredList = getFilteredList();

        if (!activeSort?.value) {
            return filteredList;
        }

        let currentSortKey = activeSort.value;

        if (currentSortKey === ListSortOptions.Name) {
            currentSortKey = ListSortOrders.AscName;
        } else if (currentSortKey === ListSortOptions.Active) {
            currentSortKey = ListSortOrders.Active;
        } else if (currentSortKey === ListSortOptions.Date) {
            currentSortKey = ListSortOrders.NewestDate;
        } else if (currentSortKey === ListSortOptions.Type) {
            currentSortKey = ListSortOrders.AscType;
        }

        if (!currentSortKey) {
            return filteredList;
        }

        switch (currentSortKey) {
            case ListSortOrders.AscName:
                filteredList.sort((a, b) => compareStringValues(a, b, alphaKey));
                break;

            case ListSortOrders.DescName:
                filteredList.sort((a, b) => compareStringValues(b, a, alphaKey));
                break;

            case ListSortOrders.NewestDate:
                filteredList.sort((a, b) => compareDateValues(a, b, dateKey));
                break;

            case ListSortOrders.OldestDate:
                filteredList.sort((a, b) => compareDateValues(b, a, dateKey));
                break;

            case ListSortOrders.AscType:
                filteredList.sort((a, b) => compareTypeValues(a, b, typeKey));
                break;

            case ListSortOrders.DescType:
                filteredList.sort((a, b) => compareTypeValues(b, a, typeKey));
                break;

            case ListSortOrders.Active:
                filteredList.sort((a, b) => compareStringValues(b, a, activeKey));
                break;

            case ListSortOrders.NotActive:
                filteredList.sort((a, b) => compareStringValues(a, b, activeKey));
                break;

            default:
                break;
        }
        return filteredList;
    });

    const debounceRemoteSearch = debounce(async (query) => {
        try {
            remoteResults.value = await remoteSearchCallback.value(query);
            debounceLogSearch();
        } catch (error) {
            console.error('Remote search failed', error);
            remoteResults.value = [];
        }
    }, REMOTE_SEARCH_DEBOUNCE_MS);

    watch(
        () => unref(searchTerm),
        (newTerm) => {
            if (useRemoteSearch.value && newTerm) {
                debounceRemoteSearch(newTerm);
            } else {
                remoteResults.value = [];
            }
        },
    );

    const debounceLogSearch = debounce(() => {
        logSearch(unref(eventParams));
    }, LOG_DEBOUNCE_MS);

    const totalCount = computed(() => {
        return items.value?.length || 0;
    });

    const visibleCount = computed(() => {
        return processedList.value.length;
    });

    const countSummaryString = computed(() => {
        const total = n(totalCount.value);
        const visible = n(visibleCount.value);
        return total === visible ? total : t('common.list.visible_of_total', { visible, total });
    });

    return {
        processedList,
        totalCount,
        visibleCount,
        countSummaryString,
        handleSortToggle,
    };
}
